/**
 * Conversions UTC ISO ↔ valeurs d'inputs `datetime-local` / `date`.
 *
 * Décision (2026-06-04) : un concours est stocké en **instant UTC** ; les inputs
 * du formulaire représentent l'**heure locale (fuseau de l'organisateur)**.
 *
 * Bug historique corrigé : `competitionToFormState` faisait `c.date.slice(0, 16)`
 * (chop de l'ISO UTC sans conversion) → l'input affichait l'heure UTC comme si
 * elle était locale, et la sauvegarde (`new Date(local).toISOString()`) la
 * reconvertissait local→UTC → **dérive de l'offset (-2h en Europe/Paris) à
 * chaque édition**. Conséquence : faux positif du verrou date côté API.
 *
 * Règle : **charger** avec `utcIsoToLocal*` (UTC→local), **sauver** avec
 * `local*ToUtcIso` (local→UTC). Le round-trip est alors stable et l'instant
 * stocké est préservé.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

/** UTC ISO → `"YYYY-MM-DDTHH:mm"` en heure locale (input `datetime-local`). */
export function utcIsoToLocalDateTimeInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `"YYYY-MM-DDTHH:mm"` (heure locale) → instant UTC ISO. */
export function localDateTimeInputToUtcIso(local: string): string {
  return new Date(local).toISOString();
}

/** UTC ISO → `"YYYY-MM-DD"` en heure locale (input `date`). */
export function utcIsoToLocalDateInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** `"YYYY-MM-DD"` (jour local) → instant UTC ISO en fin de journée locale (23:59:59). */
export function localDateInputEndOfDayToUtcIso(localDate: string): string {
  return new Date(`${localDate}T23:59:59`).toISOString();
}

/**
 * Borne un datetime-local `value` pour qu'il ne dépasse pas `max` (les deux au
 * format `"YYYY-MM-DDTHH:mm"`). Utilisé pour capper la clôture des inscriptions
 * à l'instant de début du concours (cohérence du workflow Temporal). Retourne
 * `value` inchangé si l'un des deux est vide ou si `value <= max`.
 */
export function clampLocalDateTime(value: string, max: string): string {
  if (!value || !max) return value;
  return new Date(value).getTime() > new Date(max).getTime() ? max : value;
}
